// Every number on a screen comes from the same data: stat tiles, timeline and project rows always agree.
// A port of the Mac's DaySnapshot and EntryDay (apple/Tally/App/Snapshot.swift).
import {
  billProjects, clipIntervals, mergeIntervals, reportBuckets, sumIntervals, type AgentPiece, type Interval, type ProjectTotals,
} from '../../../packages/core/src/index.ts';
import type { AgentSession, Client, Project, TimeEntry } from '../api/types.ts';
import { HOUR } from '../lib/calendar.ts';
import {
  agentPieces, billingMember, billingProjects, client, group, isQuiet, pillState, project, providerInfo, runningEntry, spanEnd,
  type PillState, type ProviderInfo, type RangeData, type Workspace,
} from './data.ts';

export interface Segment {
  start: number;
  end: number;
  kind: 'counted' | 'waiting';
  label?: string;
}

/** One agent session as the UI shows it. Subagent threads are folded into their parent. */
export interface SessionView {
  record: AgentSession;
  provider: ProviderInfo;
  state: PillState;
  /** Counted time after your corrections. */
  countedMs: number;
  waitingMs: number;
  counted: Interval[];
  waiting: Interval[];
  project: Project | undefined;
  childCount: number;
  /** Time you discarded or moved to another project. */
  discardedMs: number;
  movedMs: number;
  isLive: boolean;
  segments: Segment[];
}

export interface ProjectDay {
  project: Project;
  client: Client | undefined;
  totals: ProjectTotals;
  running: boolean;
}

export interface DaySnapshot {
  range: Interval;
  now: number;
  byProject: Map<string, ProjectTotals>;
  projectDays: ProjectDay[];
  sessions: SessionView[];
  humanLane: Interval[];
  entryCount: number;
  mergedMs: number;
  humanMs: number;
  agentMs: number;
  rawMs: number;
  waitingMs: number;
  amountCents: number;
  hasInternalTime: boolean;
  unassigned: SessionView[];
  /** Agent pieces inside the range (corrections applied). */
  pieces: AgentPiece[];
  countingAgents: number;
  waitingAgents: number;
  overlapMs: number;
  isEmpty: boolean;
}

const rank = (s: SessionView): number => (s.state === 'working' ? 0 : s.state === 'permission' ? 1 : s.state === 'waiting' ? 2 : 3);

export function buildSnapshot(ws: Workspace, data: RangeData, range: Interval, now: number): DaySnapshot {
  const clip = (list: Interval[]) => clipIntervals(list.filter((i) => i.end > i.start), range.start, range.end);

  // Your time.
  const entries = data.entries.filter((e) => e.deletedAt === null && e.startAt < range.end && (e.endAt ?? now) > range.start);
  const humanLane = clip(entries.map((e) => ({ start: e.startAt, end: e.endAt ?? now })));

  // Agent time after corrections.
  const pieces: AgentPiece[] = [];
  for (const p of agentPieces(data, now)) {
    const [i] = clipIntervals([{ start: p.start, end: p.end }], range.start, range.end);
    if (i) pieces.push({ ...p, start: i.start, end: i.end });
  }
  const piecesBySession = group(pieces, (p) => p.session);

  // Sessions (children fold into their parent for display; billing uses every piece's own project).
  const byId = new Map(data.sessions.map((s) => [s.id, s]));
  const children = group(data.sessions.filter((s) => s.parentId), (s) => s.parentId!);
  const intervals = (session: AgentSession, states: readonly string[]) =>
    clip((data.spans.get(session.id) ?? []).filter((s) => states.includes(s.state)).map((s) => ({ start: s.startAt, end: spanEnd(session, s, now) })));
  const working = (s: AgentSession) => s.state === 'working' && !isQuiet(s, now);

  const sessions: SessionView[] = [];
  for (const session of data.sessions) {
    if (session.parentId && byId.has(session.parentId)) continue;
    const kids = children.get(session.id) ?? [];
    const family = [session, ...kids];
    const mine = family.flatMap((s) => piecesBySession.get(s.id) ?? []);
    const counted = mergeIntervals(mine.map((p) => ({ start: p.start, end: p.end })));
    const engine = mergeIntervals(family.flatMap((s) => intervals(s, ['working'])));
    const waiting = intervals(session, ['waiting_input', 'waiting_permission']);
    // Recorded on a device that went quiet mid-work: not counting here until it reports again.
    const state: PillState = working(session) || kids.some(working) ? 'working' : session.state === 'working' ? 'idle' : pillState(session);
    if (engine.length === 0 && waiting.length === 0 && state !== 'working' && state !== 'permission') continue;
    const countedMs = sumIntervals(counted);
    const moved = mine.filter((p) => p.project !== session.projectId).reduce((n, p) => n + p.end - p.start, 0);
    const segments: Segment[] = [
      ...counted.map((i): Segment => ({ start: i.start, end: i.end, kind: 'counted' })),
      ...waiting.map((i): Segment => ({ start: i.start, end: i.end, kind: 'waiting' })),
    ].sort((a, b) => a.start - b.start);
    sessions.push({
      record: session, provider: providerInfo(session.provider), state, countedMs, waitingMs: sumIntervals(waiting), counted, waiting,
      project: project(ws, session.projectId), childCount: kids.length, discardedMs: Math.max(0, sumIntervals(engine) - countedMs), movedMs: moved,
      isLive: state === 'working' || state === 'permission' || state === 'waiting', segments,
    });
  }
  sessions.sort((a, b) => rank(a) - rank(b) || (b.record.lastEventAt ?? 0) - (a.record.lastEventAt ?? 0));
  const unassignedIds = new Set(pieces.filter((p) => p.project === null).map((p) => p.session));
  const unassigned = sessions.filter((s) => unassignedIds.has(s.record.id) && s.project === undefined && s.countedMs > 0);

  // Billing: merged per project, then summed across projects.
  const byProject = billProjects({
    projects: billingProjects(ws),
    entries: entries.map((e) => ({ project: e.projectId, start: e.startAt, end: e.endAt, billable: e.billable, member: billingMember(data, e.memberId) })),
    agentSpans: pieces.map((p) => ({ project: p.project, start: p.start, end: p.end, member: p.member ?? null })),
    now,
    range,
  });
  const running = runningEntry(data);
  const liveProjects = new Set(sessions.filter((s) => s.state === 'working').map((s) => s.project?.id).filter((id): id is string => !!id));
  const projectDays: ProjectDay[] = [];
  for (const totals of byProject.values()) {
    const p = project(ws, totals.project);
    if (!p) continue;
    projectDays.push({ project: p, client: client(ws, p.clientId), totals, running: running?.projectId === p.id || liveProjects.has(p.id) });
  }
  projectDays.sort((a, b) => b.totals.mergedMs - a.totals.mergedMs);
  const totals = [...byProject.values()];
  const mergedMs = totals.reduce((n, t) => n + t.mergedMs, 0);
  const rawMs = totals.reduce((n, t) => n + t.rawMs, 0);
  return {
    range, now, byProject, projectDays, sessions, humanLane, entryCount: entries.length, mergedMs,
    humanMs: sumIntervals(humanLane),
    agentMs: pieces.reduce((n, p) => n + p.end - p.start, 0),
    rawMs,
    waitingMs: sessions.reduce((n, s) => n + s.waitingMs, 0),
    amountCents: totals.reduce((n, t) => n + t.amountCents, 0),
    hasInternalTime: projectDays.some((d) => !d.project.billable && d.totals.mergedMs > 0),
    unassigned, pieces,
    countingAgents: sessions.filter((s) => s.state === 'working').length,
    waitingAgents: sessions.filter((s) => s.state === 'permission' || s.state === 'waiting').length,
    overlapMs: Math.max(0, rawMs - mergedMs),
    isEmpty: entries.length === 0 && sessions.length === 0,
  };
}

/** Hours shown on the timeline: 08–18 by default, widened to the day's activity (even hours). */
export function timelineRange(snap: DaySnapshot): Interval {
  const { range, now } = snap;
  const times = [...snap.humanLane.flatMap((i) => [i.start, i.end]), ...snap.sessions.flatMap((s) => s.segments.flatMap((g) => [g.start, g.end]))];
  let startHour = 8;
  let endHour = 18;
  if (times.length > 0) startHour = Math.min(startHour, Math.floor((Math.min(...times) - range.start) / HOUR));
  const last = Math.max(times.length > 0 ? Math.max(...times) : 0, now < range.end ? now : 0);
  if (last > range.start) endHour = Math.max(endHour, Math.ceil((last - range.start) / HOUR));
  startHour = Math.max(0, startHour - (startHour % 2));
  endHour = Math.min((range.end - range.start) / HOUR, endHour + (endHour % 2));
  const start = range.start + startHour * HOUR;
  return { start, end: Math.max(start + HOUR, Math.min(range.start + endHour * HOUR, range.end)) };
}

// MARK: Entries screens

/** Agent time of one session for one project within a day: what the entries screens show as an agent entry. */
export interface AgentBlock {
  id: string;
  session: AgentSession;
  project: Project | undefined;
  intervals: Interval[];
  day: number;
  start: number;
  end: number;
  countedMs: number;
}

/** One local day of the entries screens: your entries, the agents' time and the day's billed (merged) total. */
export interface EntryDay {
  day: Interval;
  entries: TimeEntry[];
  agents: AgentBlock[];
  billedMs: number;
  humanMs: number;
  agentMs: number;
  amountCents: number;
  isEmpty: boolean;
}

export function entryDays(ws: Workspace, data: RangeData, range: Interval, now: number, timeZone: string): EntryDay[] {
  const pieces = agentPieces(data, now);
  const sessions = new Map(data.sessions.map((s) => [s.id, s]));
  // Subagent threads fold into their parent's entry (billing keeps each piece's own project).
  const owner = (id: string): string => {
    const parent = sessions.get(id)?.parentId;
    return parent && sessions.has(parent) ? parent : id;
  };
  return reportBuckets(range, 'day', timeZone).map((day) => {
    const entries = data.entries.filter((e) => e.startAt < day.end && (e.endAt ?? now) > day.start).sort((a, b) => a.startAt - b.startAt);
    const groups = new Map<string, { session: string; project: string | null; intervals: Interval[] }>();
    for (const p of pieces) {
      const [i] = clipIntervals([{ start: p.start, end: p.end }], day.start, day.end);
      if (!i) continue;
      const session = owner(p.session);
      const key = `${session}|${p.project ?? ''}`;
      const g = groups.get(key) ?? { session, project: p.project, intervals: [] };
      g.intervals.push(i);
      groups.set(key, g);
    }
    const agents: AgentBlock[] = [];
    for (const g of groups.values()) {
      const session = sessions.get(g.session);
      if (!session) continue;
      const intervals = mergeIntervals(g.intervals);
      const first = intervals[0];
      const last = intervals[intervals.length - 1];
      agents.push({
        id: `${session.id}|${g.project ?? '-'}|${day.start}`, session, project: project(ws, g.project), intervals, day: day.start,
        start: first?.start ?? day.start, end: last?.end ?? day.start, countedMs: sumIntervals(intervals),
      });
    }
    agents.sort((a, b) => a.start - b.start);
    const snap = buildSnapshot(ws, data, day, now);
    return {
      day, entries, agents, billedMs: snap.mergedMs, humanMs: snap.humanMs, agentMs: snap.agentMs, amountCents: snap.amountCents,
      isEmpty: entries.length === 0 && agents.length === 0,
    };
  });
}

/** "2 zählen · 1 wartet" (both numbers pluralized on their own). */
export function agentCountsLabel(t: (key: string, ...args: (string | number)[]) => string, counting: number, waiting: number): string {
  if (counting === 0 && waiting === 0) return t('keine aktiv');
  return `${t('%lld zählen', counting)} · ${t('%lld warten', waiting)}`;
}
